import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, Award, Radio, Smartphone, Store, Gift, ShoppingBag, Coins,
  MousePointer, Repeat, TrendingUp, Users, Footprints, Sparkles, Heart, Check
} from "lucide-react";
import { SEO } from "../components/SEO";

const LoyaltyPage = () => {
  const navigate = useNavigate();
  return (
    <div className="pt-20" data-testid="loyalty-page">
      <SEO
        title="Loyalty & Rewards Platform"
        description="FunAsia Digital's customer loyalty platform — dual points system spanning radio, digital, and in-store. Drive repeat visits and build long-term customer relationships."
        path="/loyalty-platform"
      />

      {/* Hero */}
      <section className="section-padding bg-gradient-to-br from-orange-light via-white to-white">
        <div className="max-w-7xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 text-orange-primary font-semibold text-sm mb-4 bg-white px-4 py-2 rounded-full shadow-sm">
            <Award size={16} /> Dual Rewards System
          </span>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-gray-dark mb-6 leading-tight">
            Customer Loyalty and <span className="text-gradient-orange">Rewards Platform</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-medium mb-8 max-w-3xl mx-auto">
            A points ecosystem that spans radio, digital, and in-store — turning one-time customers
            into long-term, repeat visitors.
          </p>
          <button onClick={() => navigate("/contact")} className="btn-primary text-lg" data-testid="loyalty-cta-top">
            Activate Loyalty <ArrowRight size={20} className="inline ml-2" />
          </button>
        </div>
      </section>

      {/* OVERVIEW */}
      <section className="section-padding bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">
            <div className="lg:col-span-2">
              <span className="inline-flex items-center gap-2 text-orange-primary font-semibold text-sm mb-3 bg-orange-light px-3 py-1 rounded-full">Overview</span>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark mb-4">
                Two Sides of <span className="text-gradient-orange">One Loyalty Engine</span>
              </h2>
            </div>
            <div className="lg:col-span-3 space-y-4">
              <p className="text-lg text-gray-dark leading-relaxed">
                Most loyalty programs reward only the in-store moment. Ours rewards the journey — every
                radio listen, every ad engagement, and every store visit earns points the customer can
                redeem with you.
              </p>
              <p className="text-gray-medium leading-relaxed">
                The result is a self-reinforcing loop: customers engage with your media because it pays;
                they visit your store to redeem; they earn more by listening and engaging again. <span className="text-orange-primary font-semibold">Awareness, engagement, and visits compound.</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS — Flow */}
      <section className="section-padding bg-gradient-to-br from-gray-dark to-gray-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-3">How It Works</h2>
            <p className="text-gray-300 max-w-2xl mx-auto">Awareness on radio · Engagement on digital · Action in store · Reward at the register.</p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4">
            {[
              { icon: Radio, label: "Radio" },
              { icon: ArrowRight },
              { icon: Smartphone, label: "Digital" },
              { icon: ArrowRight },
              { icon: Store, label: "Store Visit" },
              { icon: ArrowRight },
              { icon: Coins, label: "Rewards" }
            ].map((item, idx) =>
              item.label ? (
                <div key={idx} className="flex flex-col items-center gap-2 bg-white/10 backdrop-blur-sm rounded-2xl p-5 min-w-[120px]">
                  <item.icon className="text-orange-secondary" size={32} />
                  <span className="font-medium text-sm">{item.label}</span>
                </div>
              ) : (
                <item.icon key={idx} className="text-orange-secondary" size={20} />
              )
            )}
          </div>
        </div>
      </section>

      {/* DUAL POINTS SYSTEM */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark text-center mb-10">Dual Points System</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Radio, title: "Radio Listening Points", desc: "Listeners earn points for tuning into the FunAsia radio network — building loyalty even before they visit." },
              { icon: Smartphone, title: "Digital Engagement Points", desc: "Earned through app usage, ad interactions, social engagement, and email opens." },
              { icon: Store, title: "In-Store Visit Points", desc: "Awarded for visiting and transacting at participating businesses — the deepest loyalty signal." }
            ].map((p, idx) => (
              <div key={idx} className="bg-white p-6 rounded-2xl shadow-card border border-gray-100 card-hover">
                <div className="w-14 h-14 rounded-xl gradient-orange flex items-center justify-center mb-4">
                  <p.icon className="text-white" size={28} />
                </div>
                <h3 className="font-display font-bold text-lg text-gray-dark mb-2">{p.title}</h3>
                <p className="text-gray-medium text-sm">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EARNING */}
      <section className="section-padding bg-gray-light">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark text-center mb-10">Earning Mechanisms</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Radio, label: "Listen to radio", desc: "Earn for tuning in" },
              { icon: MousePointer, label: "Engage with ads", desc: "Reward attention" },
              { icon: Smartphone, label: "Use the app", desc: "Daily streaks" },
              { icon: Store, label: "Visit partner stores", desc: "Highest payout" }
            ].map((e, idx) => (
              <div key={idx} className="bg-white p-6 rounded-2xl shadow-card text-center card-hover">
                <e.icon className="text-orange-primary mx-auto mb-3" size={32} />
                <p className="font-display font-semibold text-gray-dark">{e.label}</p>
                <p className="text-xs text-gray-medium mt-1">{e.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* REDEMPTION */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark text-center mb-10">Redemption Options</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Gift, title: "Discounts & Offers", desc: "Redeem points for instant savings at participating businesses — front-of-mind incentives." },
              { icon: ShoppingBag, title: "Free Products & Upgrades", desc: "Convert points into free items, services, or premium upgrades to drive bigger baskets." },
              { icon: Coins, title: "Exclusive Rewards", desc: "Unlock VIP perks, contests, partner experiences, and tier benefits." }
            ].map((r, idx) => (
              <div key={idx} className="bg-gradient-to-br from-orange-light to-white p-6 rounded-2xl border border-orange-primary/10 card-hover">
                <r.icon className="text-orange-primary mb-3" size={32} />
                <h3 className="font-display font-bold text-lg text-gray-dark mb-2">{r.title}</h3>
                <p className="text-gray-medium text-sm">{r.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BUSINESS BENEFITS */}
      <section className="section-padding bg-gray-light">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark text-center mb-10">Business Benefits</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Repeat, title: "Repeat Visits", desc: "Reward systems that bring customers back — predictably." },
              { icon: Heart, title: "Customer Retention", desc: "Build emotional connection beyond just transactions." },
              { icon: Sparkles, title: "Engagement Loop", desc: "Earn → redeem → engage again. Self-reinforcing." },
              { icon: TrendingUp, title: "Higher CLV", desc: "Loyal customers spend more, more often." }
            ].map((b, idx) => (
              <div key={idx} className="bg-white p-6 rounded-2xl shadow-card card-hover">
                <b.icon className="text-orange-primary mb-3" size={32} />
                <h3 className="font-display font-bold text-base text-gray-dark mb-2">{b.title}</h3>
                <p className="text-gray-medium text-sm">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="section-padding bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark text-center mb-10">Who It's For</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              "Restaurants and cafés building regulars",
              "Retail stores driving repeat baskets",
              "Salons & spas filling appointment slots",
              "Multi-location brands needing a unified loyalty system"
            ].map((item, idx) => (
              <div key={idx} className="bg-gray-light p-5 rounded-xl flex items-center gap-3">
                <Check size={20} className="text-orange-primary flex-shrink-0" />
                <span className="text-gray-dark font-medium">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding gradient-orange">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-6">Reward customers. Grow repeat business.</h2>
          <button onClick={() => navigate("/contact")} className="bg-white text-orange-primary px-8 py-4 rounded-full font-semibold text-lg hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
            Activate Loyalty <ArrowRight size={20} className="inline ml-2" />
          </button>
        </div>
      </section>
    </div>
  );
};

export default LoyaltyPage;
